import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Linking, ScrollView, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import theme from '@/config/theme';
import { APP_CONSTANTS } from '@/config/constants';
import { isIOS } from '@/utils/platform';

type UpgradePromptVariant = 'home' | 'restricted';

interface UpgradePromptProps {
  variant?: UpgradePromptVariant;
  title?: string;
  description?: string;
  highlights?: string[];
  testID?: string;
}

const HOME_COPY = {
  title: 'Desbloqueie todo o potencial do seu Guincho',
  description:
    'Oferta por tempo limitado: desbloqueie todo o fluxo de trabalho com o melhor checklist para guinchos do Brasil.',
  highlights: [
    'Checklists ilimitados.',
    'Planos com 5, 10, 15 ou 20 fotos por checklist.',
    'Opções para equipes de até 15 usuários.',
    'Suporte via WhatsApp.',
    'Armazenamento das fotos por até 1 ano.',
    'Modelos ilimitados (crie quantos quiser!).',
  ],
};

const RESTRICTED_COPY = {
  title: 'Plano Free não inclui este recurso',
  description:
    'A assinatura Premium libera checklists ilimitados, dashboards estratégicos e suporte dedicado para garantir execução impecável em campo.',
  highlights: HOME_COPY.highlights,
};

const IOS_SUBSCRIBER_COPY = {
  badge: 'App Store',
  title: 'Assine pela App Store',
  description:
    'No iPhone, o plano Premium deve ser assinado pela App Store para liberar os recursos pagos no app.',
  notice:
    'A compra e a gestao da assinatura ficam com a Apple. Usuarios com acesso empresarial ja habilitado continuam podendo entrar normalmente.',
  footer:
    'Os planos, precos e a confirmacao da compra sao fornecidos pela App Store.',
};

const getCopy = (variant: UpgradePromptVariant) =>
  variant === 'restricted' ? RESTRICTED_COPY : HOME_COPY;

const openWhatsApp = () => {
  Linking.openURL(APP_CONSTANTS.CONTACTS.WHATSAPP_URL);
};

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  variant = 'home',
  title,
  description,
  highlights,
  testID = 'upgrade-prompt',
}) => {
  const router = useRouter();
  const copy = useMemo(() => getCopy(variant), [variant]);
  const isIOSCompanionMode = isIOS;
  const headline = title || (isIOSCompanionMode ? IOS_SUBSCRIBER_COPY.title : copy.title);
  const body = description || (isIOSCompanionMode ? IOS_SUBSCRIBER_COPY.description : copy.description);
  const items = highlights || (isIOSCompanionMode ? [] : copy.highlights);
  const isWeb = Platform.OS === 'web';
  const { height } = useWindowDimensions();

  const primaryLabel = isWeb
    ? 'Ver detalhes'
    : isIOSCompanionMode
    ? 'Assinar pela App Store'
    : 'Falar com a Horizonte';

  const handlePrimary = () => {
    if (isWeb || isIOSCompanionMode) {
      router.push('/settings/subscription/plans');
      return;
    }
    openWhatsApp();
  };

  return (
    <ScrollView
      style={styles.wrapper}
      contentContainerStyle={[styles.wrapperContent, { minHeight: Math.min(height * 0.85, 520) }]}
      showsVerticalScrollIndicator={false}
      nestedScrollEnabled
      testID={testID}
    >
      <View style={styles.container}>
        <View style={styles.badge}>
          <Text style={styles.badgeText}>{isIOSCompanionMode ? IOS_SUBSCRIBER_COPY.badge : 'Promoção exclusiva'}</Text>
        </View>

        <Text style={styles.title}>{headline}</Text>
        <Text style={styles.description}>{body}</Text>

        {isIOSCompanionMode && (
          <View style={styles.noticeBox}>
            <Text style={styles.noticeTitle}>Precisa de ajuda com o acesso?</Text>
            <Text style={styles.noticeText}>{IOS_SUBSCRIBER_COPY.notice}</Text>
          </View>
        )}

        {!isIOSCompanionMode && (
          <View style={styles.priceRow}>
            <Text style={styles.oldPrice}>R$ 100,00</Text>
            <Text style={styles.newPrice}>A partir de R$ 49,90/mês</Text>
          </View>
        )}

        {items.length > 0 && (
          <View style={styles.list}>
            {items.map((item) => (
              <View key={item} style={styles.listItem}>
                <Ionicons name="checkmark-circle" size={20} color={theme.colors.util.success} />
                <Text style={styles.listItemText}>{item}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={styles.actions}>
          <TouchableOpacity style={styles.primaryButton} onPress={handlePrimary}>
            {isWeb ? (
              <Text style={styles.primaryButtonText}>{primaryLabel}</Text>
            ) : (
              <>
                <Ionicons
                  name={isIOSCompanionMode ? 'logo-apple' : 'logo-whatsapp'}
                  size={20}
                  color={theme.colors.text.inverse}
                  style={styles.primaryButtonIcon}
                />
                <Text style={styles.primaryButtonText}>{primaryLabel}</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        {!isWeb && (
          <Text style={styles.mobileFooter}>
            {isIOSCompanionMode
              ? IOS_SUBSCRIBER_COPY.footer
              : 'Assinaturas mobile são finalizadas com nossa equipe via WhatsApp oficial.'}
          </Text>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 0,
    marginHorizontal: theme.spacing.md,
    marginTop: theme.spacing.lg,
  },
  wrapperContent: {
    justifyContent: 'center',
  },
  container: {
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.background.primary,
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    ...theme.shadows.lg,
    padding: theme.spacing.lg,
  },
  badge: {
    alignSelf: 'flex-start',
    backgroundColor: `${theme.colors.primary.default}12`,
    borderRadius: theme.borderRadius.pill,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  },
  badgeText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
  },
  title: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    marginBottom: theme.spacing.sm,
  },
  description: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.md,
    lineHeight: theme.fontSizes.lg,
  },
  noticeBox: {
    marginTop: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: `${theme.colors.primary.default}22`,
    backgroundColor: `${theme.colors.primary.default}10`,
    padding: theme.spacing.md,
  },
  noticeTitle: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold as any,
    marginBottom: theme.spacing.xs,
  },
  noticeText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    lineHeight: theme.fontSizes.md,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  oldPrice: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.tertiary,
    textDecorationLine: 'line-through',
  },
  newPrice: {
    fontSize: theme.fontSizes.xxl,
    color: theme.colors.primary.default,
    fontWeight: theme.fontWeights.bold as any,
  },
  list: {
    marginTop: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
  listItemText: {
    flex: 1,
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    lineHeight: theme.fontSizes.md,
  },
  actions: {
    marginTop: theme.spacing.lg,
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  primaryButton: {
    backgroundColor: theme.colors.util.success,
    paddingVertical: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.lg,
    alignSelf: Platform.select({ web: 'center', default: 'stretch' }),
    minWidth: Platform.select({ web: 220, default: undefined }),
  },
  primaryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  primaryButtonIcon: {
    marginRight: theme.spacing.xs,
  },
  mobileFooter: {
    marginTop: theme.spacing.md,
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.xs,
    textAlign: 'center',
  },
});

export default UpgradePrompt;
